import PERMISSIONS_LIST from 'web-shared/modules/Permissions/permissions-list';
import { useUserCompanies } from 'web-shared/reducers/user-companies/hooks';
import { portalLevelPermissions } from 'web-shared/modules/Permissions/PortalLevelPermissions';
import { COMPANY_SETTING_NAME } from '../../../global/modules/companies/api/company-settings/enums/company-settings';
import { SHIPMENT_STATUSES } from '../../api/shipments/enums/shipment-statuses';
import { shipmentNotEditAbleStatuses } from './const';

const useShipmentManagementAccess = () => {
  const { isCompanySettingEnabled } = useUserCompanies();

  const hasPermission = (permission: PERMISSIONS_LIST, portalId?: number | null) => {
    return portalLevelPermissions.isPermissionEnabled(permission, portalId);
  };

  const hasLegacySMAccess = () => {
    return isCompanySettingEnabled(COMPANY_SETTING_NAME.SHIPMENT_MANAGER_LEGACY_ACCESS)
      && hasPermission(PERMISSIONS_LIST.simpl_access);
  };
  const hasBetaSMAccess = () => {
    return isCompanySettingEnabled(COMPANY_SETTING_NAME.SHIPMENT_MANAGER_ACCESS)
      && hasPermission(PERMISSIONS_LIST.simpl_access);
  };

  const hasSMAppAccess = () => hasLegacySMAccess() || hasBetaSMAccess();

  const hasShipmentCreatePageAccess = () => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_manage);
  };

  const hasManageShipmentAccess = (portalId: number) => hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_manage, portalId);

  const hasEditShipmentAccess = <T extends { portal_id: number; state: SHIPMENT_STATUSES; sts_trackable: boolean }>(shipment: T) => {
    return (
      shipment.sts_trackable &&
      !shipmentNotEditAbleStatuses.includes(shipment.state) &&
      hasSMAppAccess() &&
      (hasPermission(PERMISSIONS_LIST.shipments_edit, shipment.portal_id) || hasManageShipmentAccess(shipment.portal_id))
    );
  };

  const hasBulkUpdateShipmentsAccess = () => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.bulk_update_shipments_status);
  };

  const hasRemoveShipmentAttachmentsAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.remove_shipment_attachments, portalId);
  };

  const hasViewActivityLogAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.activity_log_view, portalId);
  };

  const hasViewShipmentMessagesAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_messages, portalId);
  };

  const hasTrackAndTraceAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.track_and_trace_view, portalId);
  };

  const hasViolationApproveAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.violation_approve, portalId);
  };

  const hasCloseShipmentAccess = (portalId: number) => {
    return hasSMAppAccess() && (hasPermission(PERMISSIONS_LIST.shipments_close, portalId));
  };

  const hasReopenShipmmentAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_reopen, portalId);
  };

  const hasMoveToAtPickupAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_change_status_to_at_pickup, portalId);
  };

  const hasMoveToPickedUpAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_move_to_picked_up, portalId);
  };

  const hasMoveForewardToInTransitAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_move_to_in_transit, portalId);
  };

  const hasMoveBackwardToInTransitAccess = (portalId: number) => {
    return hasMoveForewardToInTransitAccess(portalId) && hasPermission(PERMISSIONS_LIST.shipments_move_between_in_transit_and_delivery_states, portalId);
  };

  const hasMoveBetweenInTransitAndDeliveryAccess = (portalId: number) => {
    return hasSMAppAccess() && hasPermission(PERMISSIONS_LIST.shipments_move_between_in_transit_and_delivery_states, portalId);
  };

  const hasShipmentTemperatureReportAccess = (portalId: number) => {
    return hasPermission(PERMISSIONS_LIST.shipment_temperature_power_bi_report_access, portalId);
  };

  const hasChainOfCustodyReportAccess = (portalId: number) => {
    return hasPermission(PERMISSIONS_LIST.chain_of_custody_power_bi_report_access, portalId);
  };

  const hasCreateNewCarriersAccess = (portalId: number) => {
    return hasPermission(PERMISSIONS_LIST.create_new_carriers, portalId);
  };

  const hasQualityReviewAccess = () => {
    return hasPermission(PERMISSIONS_LIST.quality_review_page_access);
  };

  const hasShipmentQualityReviewAccess = <T extends { portal_id: number; workflow_group: { quality_generate_shipment_qr_code: boolean }; portal_settings: { quality_feature_access: boolean } }>(shipment: T) => {
    return shipment.workflow_group.quality_generate_shipment_qr_code
      && shipment.portal_settings.quality_feature_access
      && hasPermission(PERMISSIONS_LIST.quality_review_page_access, shipment.portal_id);
  };

  const hasAccessToQualityReviewActions = (portalId: number) => {
    return hasPermission(PERMISSIONS_LIST.quality_review_accept_reject_shipment, portalId);
  };

  const hasAccessToQRStatusChangeActions = (portalId: number) => {
    return hasQualityReviewAccess() && hasPermission(PERMISSIONS_LIST.quality_review_status_update, portalId);
  };

  const hasEnableEscortConnectAccess = (portalId: number) => {
    return hasManageShipmentAccess(portalId) &&
      hasPermission(PERMISSIONS_LIST.enable_overhaul_contracted_escorts, portalId);
  };

  return {
    hasShipmentCreatePageAccess,
    hasLegacySMAccess,
    hasBetaSMAccess,
    hasSMAppAccess,
    hasBulkUpdateShipmentsAccess,
    hasEditShipmentAccess,
    hasManageShipmentAccess,
    hasRemoveShipmentAttachmentsAccess,
    hasViewActivityLogAccess,
    hasViewShipmentMessagesAccess,
    hasTrackAndTraceAccess,
    //shipment status change actions
    hasCloseShipmentAccess,
    hasReopenShipmmentAccess,
    hasMoveToAtPickupAccess,
    hasMoveToPickedUpAccess,
    hasMoveForewardToInTransitAccess,
    hasMoveBackwardToInTransitAccess,
    hasMoveBetweenInTransitAndDeliveryAccess,
    hasViolationApproveAccess,
    hasShipmentTemperatureReportAccess,
    hasChainOfCustodyReportAccess,
    hasCreateNewCarriersAccess,
    hasQualityReviewAccess,
    hasShipmentQualityReviewAccess,
    hasAccessToQualityReviewActions,
    hasAccessToQRStatusChangeActions,
    hasEnableEscortConnectAccess,
  };
};

export default useShipmentManagementAccess;